import type { Automation } from '@repo/api-codegen';
import { makeAutoObservable } from 'mobx';

export enum TriggerBlockType {
	SCHEMA = 'SCHEMA_CHANGE',
	SCHEDULE = 'SCHEDULE',
}

export enum ActionBlockType {
	FILTER_RESOURCES = 'FILTER_RESOURCES',
	GENERATE_AI_OUTPUT = 'GENERATE_AI_OUTPUT',
	EDIT_RESOURCES = 'EDIT_RESOURCES',
	PROPAGATE_METADATA = 'PROPAGATE_METADATA',
	SEND_ANNOUNCEMENT = 'SEND_ANNOUNCEMENT',
	SEND_SLACK_MESSAGE = 'SEND_SLACK_MESSAGE',
	SEND_EMAIL = 'SEND_EMAIL',
}

export class AutomationPageV2Store {
	automation?: Automation;
	triggerBlockType?: TriggerBlockType;
	actionBlocks: ActionBlockType[] = [];
	hasActionBlocks: boolean = false;
	isFilterResourcesBlockVisible: boolean = false;
	isEditResourcesBlockVisible: boolean = false;
	isPropagateMetadataBlockVisible: boolean = false;
	isSendAnnouncementBlockVisible: boolean = false;
	isSendSlackMessageBlockVisible: boolean = false;
	isSendEmailBlockVisible: boolean = false;
	isGenerateAiOutputBlockVisible: boolean = false;
	isBlockExhausted: boolean = false;
	isBlockFull: boolean = false;
	remainingActionBlocks: ActionBlockType[] = [];
	automationAiGenerationEnabled: boolean = false;
	isFilterBlockRequired: boolean = false;
	isEditResourcesBlockRequired: boolean = false;
	hasAiOutput: boolean = false;

	constructor(automationAiGenerationEnabled: boolean) {
		makeAutoObservable(this);
		this.automationAiGenerationEnabled = automationAiGenerationEnabled;
		this.remainingActionBlocks = [
			ActionBlockType.FILTER_RESOURCES,
			ActionBlockType.GENERATE_AI_OUTPUT,
			ActionBlockType.EDIT_RESOURCES,
			ActionBlockType.PROPAGATE_METADATA,
			ActionBlockType.SEND_ANNOUNCEMENT,
			ActionBlockType.SEND_SLACK_MESSAGE,
			ActionBlockType.SEND_EMAIL,
		];

		if (!this.automationAiGenerationEnabled) {
			this.remainingActionBlocks = this.remainingActionBlocks.filter(
				(blockType) => blockType !== ActionBlockType.GENERATE_AI_OUTPUT
			);
		}
	}

	private setUpBlocks() {
		this.setTriggerBlockType(this.automation?.trigger_type as TriggerBlockType);
		const isEditBlockVisible =
			this.automation?.action_update_entities &&
			this.automation?.action_update_entities.length > 0;

		const isGenerateAiOutputBlockVisible =
			this.automationAiGenerationEnabled &&
			this.automation?.ai_settings &&
			this.automation.ai_settings.length > 0;

		if (this.automation?.filter_settings) {
			this.addActionBlockType(ActionBlockType.FILTER_RESOURCES);
		}

		if (isEditBlockVisible) {
			this.addActionBlockType(ActionBlockType.EDIT_RESOURCES);
		}

		if (isGenerateAiOutputBlockVisible) {
			this.addActionBlockType(ActionBlockType.GENERATE_AI_OUTPUT);
		}

		if (this.automation?.action_propagate_metadata) {
			this.addActionBlockType(ActionBlockType.PROPAGATE_METADATA);
		}

		if (this.automation?.action_send_announcement) {
			this.addActionBlockType(ActionBlockType.SEND_ANNOUNCEMENT);
		}

		if (this.automation?.action_send_slack_message) {
			this.addActionBlockType(ActionBlockType.SEND_SLACK_MESSAGE);
		}

		if (this.automation?.action_send_email) {
			this.addActionBlockType(ActionBlockType.SEND_EMAIL);
		}

		this.checkBlockState();
	}

	setAutomation(automation: Automation) {
		this.automation = automation;
		this.setUpBlocks();
	}

	setTriggerBlockType(triggerBlockType?: TriggerBlockType) {
		this.triggerBlockType = triggerBlockType;
	}

	addActionBlockType(actionBlockType: ActionBlockType) {
		if (this.actionBlocks.includes(actionBlockType)) {
			return;
		}

		this.actionBlocks.push(actionBlockType);
		this.hasActionBlocks = true;
		this.remainingActionBlocks = this.remainingActionBlocks.filter(
			(blockType) => !this.actionBlocks.includes(blockType)
		);
		this.updateActionBlockVisibility();
		this.checkBlockState();
	}

	removeActionBlockType(actionBlockType: ActionBlockType) {
		let removedBlocks: ActionBlockType[] = [];

		this.actionBlocks = this.actionBlocks.filter(
			(blockType) => blockType !== actionBlockType
		);

		removedBlocks.push(actionBlockType);

		if (this.actionBlocks.length === 0) {
			this.hasActionBlocks = false;
		}

		const remainingBlocks = [
			...new Set([...this.remainingActionBlocks, ...removedBlocks]),
		];

		this.remainingActionBlocks = remainingBlocks;

		this.updateActionBlockVisibility();
		this.checkBlockState();
	}

	private updateActionBlockVisibility() {
		this.isFilterResourcesBlockVisible = this.actionBlocks.includes(
			ActionBlockType.FILTER_RESOURCES
		);

		this.isEditResourcesBlockVisible = this.actionBlocks.includes(
			ActionBlockType.EDIT_RESOURCES
		);

		this.isPropagateMetadataBlockVisible = this.actionBlocks.includes(
			ActionBlockType.PROPAGATE_METADATA
		);

		this.isSendAnnouncementBlockVisible = this.actionBlocks.includes(
			ActionBlockType.SEND_ANNOUNCEMENT
		);

		this.isSendSlackMessageBlockVisible = this.actionBlocks.includes(
			ActionBlockType.SEND_SLACK_MESSAGE
		);

		this.isSendEmailBlockVisible = this.actionBlocks.includes(
			ActionBlockType.SEND_EMAIL
		);

		this.isGenerateAiOutputBlockVisible = this.actionBlocks.includes(
			ActionBlockType.GENERATE_AI_OUTPUT
		);
	}

	private checkBlockState() {
		this.isBlockExhausted = this.remainingActionBlocks.length === 0;
		this.isBlockFull = this.automationAiGenerationEnabled
			? this.remainingActionBlocks.length === 7
			: this.remainingActionBlocks.length === 6;

		if (this.actionBlocks.includes(ActionBlockType.FILTER_RESOURCES)) {
			this.isFilterBlockRequired = false;
		}

		if (this.actionBlocks.includes(ActionBlockType.EDIT_RESOURCES)) {
			this.isEditResourcesBlockRequired = false;
		}

		if (
			!this.actionBlocks.includes(ActionBlockType.FILTER_RESOURCES) &&
			(this.actionBlocks.includes(ActionBlockType.GENERATE_AI_OUTPUT) ||
				this.actionBlocks.includes(ActionBlockType.EDIT_RESOURCES) ||
				this.actionBlocks.includes(ActionBlockType.PROPAGATE_METADATA))
		) {
			this.isFilterBlockRequired = true;
		}

		if (
			!this.actionBlocks.includes(ActionBlockType.EDIT_RESOURCES) &&
			this.actionBlocks.includes(ActionBlockType.GENERATE_AI_OUTPUT) &&
			this.automation?.ai_settings &&
			this.automation.ai_settings.length > 0
		) {
			this.isEditResourcesBlockRequired = true;
		}
	}

	setHasAiOutput(hasAiOutput: boolean) {
		this.hasAiOutput = hasAiOutput;
		this.checkBlockState();
	}
}
